import { useTranslation } from "react-i18next";
import { DeleteOutlined } from "@ant-design/icons";
import type { SchemaElement, SchemaRegion } from "../../../api/restaurantSchema";
import {
  PanelDivider,
  PanelField,
  PanelHeading,
  PanelInput,
  RegionSelect,
  SeatsPicker,
} from "./tablesPanelControls";
import { DANGER_BUTTON, SECONDARY_BUTTON } from "./tablesPanelStyles";
import { useTableNameDraft } from "./useTableNameDraft";
import type { TablesConfigActions } from "./useTablesConfigActions";

/**
 * Панелът на ЕДНА избрана маса: номер (запис с Enter / „Запази"; отказ от BE
 * връща стария номер), места (запис веднага), зала (само при повече от един
 * регион — масата отива на първото свободно място там), изтриване.
 */
const TableEditPanel = ({
  table,
  regions,
  actions,
  onDelete,
  onClose,
}: {
  table: SchemaElement;
  regions: SchemaRegion[];
  actions: TablesConfigActions;
  onDelete: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const draft = useTableNameDraft(table, regions, (current, name) => void actions.renameTable(current, name));
  const isNameChanged = draft.name.trim() !== (table.name ?? "");
  const regionName = regions.find((region) => region.id === table.regionId)?.name ?? "";

  return (
    <div className="flex flex-col gap-5">
      <PanelHeading
        kicker={regionName}
        title={t("tablesConfig.edit.title", { name: table.name ?? "" })}
        onClose={onClose}
      />

      <PanelField label={t("tablesConfig.newTables.number")} error={draft.error}>
        <div className="flex gap-2">
          <PanelInput
            value={draft.name}
            onChange={draft.setName}
            onEnter={draft.commit}
            // Непроменен номер → Esc не се поглъща (затваря sheet-а/избора).
            onEscape={draft.name !== (table.name ?? "") ? draft.cancel : undefined}
            invalid={!!draft.error}
            ariaLabel={t("tablesConfig.newTables.number")}
          />
          {isNameChanged && (
            <button
              type="button"
              onClick={draft.commit}
              disabled={!!draft.error || !draft.name.trim()}
              className="h-11 shrink-0 cursor-pointer rounded-[10px] border-none bg-brand px-4 text-[13.5px] font-bold text-white transition-colors duration-150 hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("tablesConfig.edit.save")}
            </button>
          )}
        </div>
      </PanelField>

      <PanelField label={t("tablesConfig.edit.seats")}>
        <SeatsPicker value={table.seats ?? 1} onChange={(seats) => void actions.changeSeats(table, seats)} />
      </PanelField>

      {regions.length > 1 && (
        <PanelField label={t("tablesConfig.edit.region")}>
          <RegionSelect
            regions={regions}
            value={table.regionId}
            onChange={(target) => void actions.moveTable(table, target)}
            ariaLabel={t("tablesConfig.edit.region")}
          />
        </PanelField>
      )}

      <PanelDivider />
      <div className="flex flex-col gap-2">
        <button type="button" onClick={onDelete} className={DANGER_BUTTON}>
          <DeleteOutlined className="text-[14px]" />
          {t("tablesConfig.edit.delete")}
        </button>
        <button type="button" onClick={onClose} className={SECONDARY_BUTTON}>
          {t("tablesConfig.edit.close")}
        </button>
      </div>
    </div>
  );
};

export default TableEditPanel;
